import {
    ChatPanel,
    ComposePanel,
    FilterMethod,
    IComment,
    ICustomFilter,
    IMessage,
    ListProviderMessagesOrderEnum,
    MessageListPanel,
    MessagingProvider,
    ProviderClaim,
    ProviderGuarantor,
    ProviderMessage,
    ProviderMessageSortType,
    ToastContext,
} from '@enter/core';
import { usePdfView } from 'components';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React, { useContext, useEffect, useState } from 'react';
import { DialogContext } from 'services';
import { IInjectedRootStore, IMessageListingRequest } from 'stores';
import { MessagingPresenter } from './messaging.presenter';
import * as S from './messaging.styles';

type IMessagingProps = {
    presenter: MessagingPresenter;
    claimId?: string;
    guarantorId?: string;
    messageId?: string;
    onClose: () => void;
    onAssignDocument?: () => void;
    defaultSubject?: string;
    defaultBody?: string;
};

export const ProviderMessaging: React.FunctionComponent<IMessagingProps> = inject('rootStore')(
    observer((props: IMessagingProps) => {
        const injectedProps = props as IInjectedRootStore & IMessagingProps;
        const { rootStore, presenter, claimId, guarantorId } = injectedProps;
        if (!rootStore) {
            return <></>;
        }

        const { roleId } = rootStore;
        const isReadOnly: boolean = roleId === 'PROVIDER_READ_ONLY';
        const isReadOnlyClaimChoice: boolean = true;

        const dialogContext = useContext(DialogContext);
        const toastContext = useContext(ToastContext);

        const [isLoading, setLoading] = useState(false);
        const [isResourceLoading, setResourceLoading] = useState(false);
        const [claims, setClaims] = useState<ProviderClaim[]>([]);
        const [messages, setMessages] = useState<ProviderMessage[]>([]);
        const [filter, setFilter] = useState<ICustomFilter>();
        const [isFilterEnabled, setFilterEnabled] = useState(true);
        // const [fetchOptions, setFetchOptions] = useState<ITableFetchOptions>();
        const [openFile] = usePdfView();

        const { isOpen, isDetailLoading, messageDetails } = presenter;
        const { inboxTotal } = rootStore.messageStore;

        useEffect(() => {
            (async () => {
                let filterRes: ICustomFilter | undefined;
                if (claimId) {
                    setResourceLoading(true);
                    const claim = await rootStore.claimStore.loadClaim(claimId);
                    setResourceLoading(false);

                    if (claim) {
                        filterRes = generateClaimFilter(claim);
                    }
                }

                if (guarantorId) {
                    setResourceLoading(true);
                    const guarantor = await rootStore.guarantorStore.loadGuarantor(guarantorId);
                    setResourceLoading(false);

                    filterRes = generateGuarantorFilter(guarantor);
                }

                setFilter(filterRes);
                refreshMessageListing(filterRes);
            })();
        }, [claimId, guarantorId]);

        const generateClaimFilter = (claim: ProviderClaim): ICustomFilter => {
            return {
                filterType: 'AsyncSelect',
                filterId: 'filterClaimId',
                label: 'Filter Claim',
                filterMethod: FilterMethod.EQ,
                value: {
                    label: `${claim.patientFirst} ${claim.patientLast}`,
                    value: claimId,
                },
            };
        };

        const generateGuarantorFilter = (guarantor: ProviderGuarantor): ICustomFilter => {
            return {
                filterType: 'AsyncSelect',
                filterId: 'filterGuarantorId',
                label: 'Filter Guarantor',
                filterMethod: FilterMethod.EQ,
                value: {
                    label: `${guarantor?.first} ${guarantor?.last}`,
                    value: guarantor.id!,
                },
            };
        };

        const renderChatPanel = (): React.ReactElement => {
            if (presenter.isComposeOpen && !isReadOnly) {
                // const historyId = _.get(history, 'location.state.claimId');
                // const hrefId = getClaimId(window.location.href);
                let subject = _.get(history, 'location.state.subject');
                let body = _.get(history, 'location.state.body');

                if(!subject && props.defaultSubject) {
                    subject = props.defaultSubject;
                }

                if(!body && props.defaultBody) {
                    body = props.defaultBody;
                }

                return (
                    <S.ChatPanelContainer>
                        <ComposePanel
                            claimId={claimId}
                            subject={subject}
                            body={body}
                            forceClaim={!!claimId}
                            hideNote={true}
                        />
                    </S.ChatPanelContainer>
                );
            }

            if (presenter.messageDetails) {
                return (
                    <S.ChatPanelContainer>
                        <ChatPanel onBack={handleCloseMessageDetails} hideNote={true} />
                    </S.ChatPanelContainer>
                );
            }

            const sortedMessages = _.sortBy(messages || [], 'open').reverse();
            return (
                <S.ChatPanelContainer>
                    <MessageListPanel
                        inboxCount={inboxTotal}
                        inboxMessages={sortedMessages}
                        onFetchData={() => Promise.resolve()}
                        onShowCompose={handleShowCompose}
                        selectedMessageId={undefined}
                        filter={isFilterEnabled ? filter : undefined}
                        onFilterRemove={handleFilterRemove}
                    />
                </S.ChatPanelContainer>
            );
        };

        const refreshMessageListing = async (filter?: ICustomFilter): Promise<void> => {
            setLoading(true);

            const inboxReq = await buildRequest(filter);
            const messages = await rootStore.messageStore.loadMessageListing(inboxReq);

            setMessages(messages.messages.reverse());
            setLoading(false);
        };

        const refreshMessageDetail = async (mid: string): Promise<void> => {
            presenter.openMessage(mid);
        };

        const refresh = async (ids: string | string[]): Promise<void> => {
            // multiple IDs from selection in message-list?
            if (_.isArray(ids)) {
                await refreshMessageListing();
            } else {
                await refreshMessageDetail(ids);
            }
        };

        const handleShowCompose = (): void => {
            presenter.setComposeOpen(true);
        };

        const buildRequest = async (filter?: ICustomFilter): Promise<IMessageListingRequest> => {
            const sort: ProviderMessageSortType[] = [ProviderMessageSortType.UPDATED];
            const order: ListProviderMessagesOrderEnum[] = [ListProviderMessagesOrderEnum.DESCENDING];

            const claimFilter = filter?.filterId === 'filterClaimId' ? filter : undefined;
            const guarantorFilter = filter?.filterId === 'filterGuarantorId' ? filter : undefined;

            const filters = [claimFilter, guarantorFilter];

            // if (guarantorFilter?.value) {
            //     const id = (guarantorFilter?.value as ISelectOption<string>).value;
            //     if (id) {
            //         const claims = await rootStore.claimStore.getGuarantorClaims(id);
            //         if (claims && claims.length > 0) {
            //             claims.forEach(c => {
            //                 filters.push({
            //                     filterType: 'AsyncSelect',
            //                     filterId: 'filterClaimId',
            //                     filterMethod: FilterMethod.EQ,
            //                     value: {
            //                         label: c.id || 'Claim',
            //                         value: c.id,
            //                     },
            //                 });
            //             });
            //         }
            //     }
            // }

            return {
                page: 0,
                pageSize: 100,
                sort,
                order,
                filters: _.compact(filters),
            };
        };

        const handleCloseMessageDetails = (): void => {
            presenter.closeDetails();
        };

        const claimSearch = async (term: string): Promise<ProviderClaim[]> => {
            console.log("components claimSearch");
            return await doSearch(term);
        };

        // Create a debounced function
        const debouncedDoSearch = _.debounce(async (term, resolve, reject) => {
            try {
                setClaims([]);
                setLoading(true);
                const results = await rootStore.claimStore.loadBasicClaimSearchResults(term);
                setClaims(results);
                setLoading(false);
                resolve(results);  // Resolve the promise with the results
            } catch (error) {
                reject(error);  // Reject the promise if there's an error
            }
        }, 800);

        // Wrapper function that returns a Promise
        const doSearch = async (term: string): Promise<ProviderClaim[]> => {
            return new Promise((resolve, reject) => {
                debouncedDoSearch(term, resolve, reject);
            });
        };

        const composeComment = async ({ messageId: mid, body, files }: IComment): Promise<void> => {
            try {
                await rootStore.messageStore.addComment(rootStore.account, mid, body, files);
                await refreshMessageDetail(mid);
            } catch (err) {
                dialogContext.showError(err);
            }
        };

        const composeMessage = async ({ subject, body, note, claimId, files }: IMessage): Promise<void> => {
            try {
                await rootStore.messageStore.composeMessage(rootStore.account, subject, body, claimId, files);
                await refresh([]);
                toastContext.notify?.('Message sent', 'right');
            } catch (err) {
                dialogContext.showError(err);
            }
        };

        const downloadDocument = async (id: string): Promise<void> => {
            const file = await rootStore.messageStore.embed(id);

            file.url && openFile(file.url);
        };

        const messageOpen = async (messageId: string): Promise<void> => {
            presenter.openMessage(messageId);

            // mark read without waiting
            rootStore.messageStore.markAsRead(messageId);
        };

        const messageClose = async (): Promise<void> => {
            presenter.setComposeOpen(false);
            props.onClose();
        };

        const markAsOpen = async (ids: string | string[]): Promise<void> => {
            const promises = _.map(
                _.concat([], ids),
                async (id): Promise<void> => rootStore.messageStore.markAsOpen(id),
            );
            await Promise.all(promises);
            const updatedMessages = messages.map(message => {
                if (ids.includes(message.id!)) {
                    message.open = true;
                }

                return message;
            });
            setMessages(updatedMessages);
            await refresh(ids);
        };

        const markAsClosed = async (ids: string | string[]): Promise<void> => {
            const promises = _.map(
                _.concat([], ids),
                async (id): Promise<void> => rootStore.messageStore.markAsClosed(id),
            );
            await Promise.all(promises);
            const updatedMessages = messages.map(message => {
                if (ids.includes(message.id!)) {
                    message.open = false;
                }

                return message;
            });
            setMessages(updatedMessages);

            await refresh(ids);
        };

        const markAsRead = async (ids: string | string[]): Promise<void> => {
            const promises = _.map(
                _.concat([], ids),
                async (id): Promise<void> => rootStore.messageStore.markAsRead(id),
            );
            await Promise.all(promises);
            const updatedMessages = messages.map(message => {
                if (ids.includes(message.id!)) {
                    message.read = true;
                }

                return message;
            });
            setMessages(updatedMessages);

            await refresh(ids);
        };

        const handleFilterRemove = () => {
            setFilterEnabled(false);
            refreshMessageListing();
        };

        return (
            <MessagingProvider
                isReadOnlyClaimChoice={isReadOnlyClaimChoice}
                isReadOnly={isReadOnly}
                isListLoading={isLoading || isDetailLoading || isResourceLoading}
                isDetailLoading={isDetailLoading}
                account={rootStore.account}
                enableMessageTypeValidation={true}
                message={messageDetails ? messageDetails.message : undefined}
                comments={messageDetails ? messageDetails.comments : undefined}
                documents={messageDetails ? messageDetails.documents : undefined}
                claims={claims}
                onClaimSearch={claimSearch}
                onComposeComment={composeComment}
                onComposeMessage={composeMessage}
                onDocumentDownload={downloadDocument}
                onMarkAsClosed={markAsClosed}
                onMarkAsOpen={markAsOpen}
                onMarkAsRead={markAsRead}
                onMessageOpen={messageOpen}
                onMessageClose={messageClose}>
                <>{isOpen && renderChatPanel()}</>
            </MessagingProvider>
        );
    }),
);
